import {Box, Button, Stack, Typography} from '@customink/pigment-react';
import {CircularProgress} from '@mui/material';
import React, {useMemo} from 'react';
import {useFormContext} from 'react-hook-form';
import {toMmsUrl, withParams} from '../../../../utils/imageUtils';
import useImageLoading from '../hooks/useImageLoading';
import {getEffectiveHash} from '../utils/categoryImageParser';
import {buildEditableParams} from '../utils/parameterUtils';

export default function ImagePreview({
  categoryImages,
  colors,
  isLoading,
  onBack,
  onSubmit,
  previewUrl,
  primaryCategoryId,
  styleCardImageUrl
}) {
  const {formState, getValues} = useFormContext();
  const {isSubmitting, isValid} = formState;

  const formValues = getValues();

  // Generate URLs for dual display when lab image is disabled
  const {colorImageUrl, laydownImageUrl} = useMemo(() => {
    if (!formValues.useLabImage && styleCardImageUrl) {
      // Use first color if colorId is null/empty, otherwise use specified colorId
      let effectiveColorId = formValues.colorId;

      // If no colorId is specified (null or empty), use the first available color
      if (!effectiveColorId && colors?.[0]?.id != null) {
        effectiveColorId = String(colors[0].id);
      }

      if (effectiveColorId) {
        // Generate color image URL with all image processing parameters
        const hash = getEffectiveHash(
          styleCardImageUrl,
          categoryImages,
          primaryCategoryId
        );

        const colorImagePath = `mms/images/catalog/${hash}/colors/${effectiveColorId}/views/alt/front_medium_extended.png`;
        const params = buildEditableParams(formValues);
        const colorUrl = withParams(toMmsUrl(colorImagePath), params);

        // Laydown image URL (unchanged, no processing parameters)
        const laydownUrl = toMmsUrl(styleCardImageUrl);

        return {
          colorImageUrl: colorUrl,
          laydownImageUrl: laydownUrl
        };
      }
    }

    return {colorImageUrl: null, laydownImageUrl: null};
  }, [
    formValues.useLabImage,
    formValues.colorId,
    formValues,
    styleCardImageUrl,
    categoryImages,
    colors,
    primaryCategoryId
  ]);

  // Use stable loading hooks to prevent flickering
  const labImageLoading = useImageLoading(previewUrl);
  const colorImageLoading = useImageLoading(colorImageUrl);
  const laydownImageLoading = useImageLoading(laydownImageUrl);

  // Note: Using stable hooks prevents flickering when toggling between modes

  const isLoadingOrSubmitting = isLoading || isSubmitting;

  return (
    <Box sx={{flex: 1, position: 'sticky', top: 16}}>
      <Typography variant="h6" sx={{mb: 2}}>
        Image Preview
      </Typography>

      <Box
        sx={{
          border: '1px solid #0000001c',
          borderRadius: 2,
          mb: 2,
          overflow: 'hidden'
        }}>
        <Box
          sx={{
            alignItems: formValues.useLabImage ? 'center' : 'stretch',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '30vh',
            padding: formValues.useLabImage ? 0 : 0.5,
            position: 'relative'
          }}>
          {/* Lab Image Mode - Single Image */}
          {formValues.useLabImage && previewUrl && isValid && (
            <>
              <img
                key={`lab-${previewUrl}`}
                ref={labImageLoading.imageRefCallback}
                src={previewUrl}
                alt="Lab preview"
                style={{
                  display:
                    labImageLoading.imageLoading || labImageLoading.imageError
                      ? 'none'
                      : 'block',
                  maxWidth: '100%',
                  minHeight: '30vh',
                  objectFit: 'contain'
                }}
                onLoad={labImageLoading.handleImageLoad}
                onError={labImageLoading.handleImageError}
              />

              {/* Loading state for lab image */}
              {labImageLoading.imageLoading && (
                <Stack
                  alignItems="center"
                  gap="0.5rem"
                  sx={{
                    left: '50%',
                    position: 'absolute',
                    top: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%'
                    }}>
                    <CircularProgress />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Loading lab image preview...
                  </Typography>
                </Stack>
              )}

              {/* Error state for lab image */}
              {labImageLoading.imageError && (
                <Stack
                  alignItems="center"
                  spacing={2}
                  sx={{
                    left: '50%',
                    position: 'absolute',
                    top: '50%',
                    transform: 'translate(-50%, -50%)'
                  }}>
                  <Typography variant="body1" color="error">
                    ⚠️ Failed to load lab image
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    The lab image could not be loaded. Please check the
                    configuration.
                  </Typography>
                </Stack>
              )}
            </>
          )}

          {/* Standard Mode - Dual Images */}
          {!formValues.useLabImage &&
            colorImageUrl &&
            laydownImageUrl &&
            isValid && (
              <Stack
                direction={{md: 'row', xs: 'column'}}
                spacing={1}
                sx={{
                  height: {md: 'calc(30vh - 16px)', xs: 'auto'},
                  maxWidth: '800px',
                  mx: 'auto',
                  width: '100%'
                }}>
                {/* Initial Product Image (Laydown) */}
                <Box sx={{display: 'flex', flex: 1, flexDirection: 'column'}}>
                  <Typography
                    variant="subtitle2"
                    sx={{mb: 0.5, textAlign: 'center'}}>
                    Initial Product Image
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      flex: 1,
                      minHeight: {md: '250px', xs: '200px'},
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                    <img
                      key={`laydown-${laydownImageUrl}`}
                      ref={laydownImageLoading.imageRefCallback}
                      src={laydownImageUrl}
                      alt="Laydown preview"
                      style={{
                        display:
                          laydownImageLoading.imageLoading ||
                          laydownImageLoading.imageError
                            ? 'none'
                            : 'block',
                        height: '100%',
                        objectFit: 'contain',
                        width: '100%'
                      }}
                      onLoad={laydownImageLoading.handleImageLoad}
                      onError={laydownImageLoading.handleImageError}
                    />

                    {/* Loading state for laydown image */}
                    {laydownImageLoading.imageLoading && (
                      <Box
                        sx={{
                          left: '50%',
                          position: 'absolute',
                          top: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}

                    {/* Error state for laydown image */}
                    {laydownImageLoading.imageError && (
                      <Box
                        sx={{
                          left: '50%',
                          position: 'absolute',
                          textAlign: 'center',
                          top: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}>
                        <Typography variant="caption" color="error">
                          Failed to load
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Color Image */}
                <Box sx={{display: 'flex', flex: 1, flexDirection: 'column'}}>
                  <Typography
                    variant="subtitle2"
                    sx={{mb: 0.5, textAlign: 'center'}}>
                    Color Image (Image settings preview)
                  </Typography>
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      flex: 1,
                      minHeight: {md: '250px', xs: '200px'},
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                    <img
                      key={`color-${colorImageUrl}`}
                      ref={colorImageLoading.imageRefCallback}
                      src={colorImageUrl}
                      alt="Color preview"
                      style={{
                        display:
                          colorImageLoading.imageLoading ||
                          colorImageLoading.imageError
                            ? 'none'
                            : 'block',
                        height: '100%',
                        objectFit: 'contain',
                        width: '100%'
                      }}
                      onLoad={colorImageLoading.handleImageLoad}
                      onError={colorImageLoading.handleImageError}
                    />

                    {/* Loading state for color image */}
                    {colorImageLoading.imageLoading && (
                      <Box
                        sx={{
                          left: '50%',
                          position: 'absolute',
                          top: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}

                    {/* Error state for color image */}
                    {colorImageLoading.imageError && (
                      <Box
                        sx={{
                          left: '50%',
                          position: 'absolute',
                          textAlign: 'center',
                          top: '50%',
                          transform: 'translate(-50%, -50%)'
                        }}>
                        <Typography variant="caption" color="error">
                          Failed to load
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Stack>
            )}

          {/* No preview state - show only when no valid preview can be displayed */}
          {!(
            (formValues.useLabImage && previewUrl && isValid) ||
            (!formValues.useLabImage &&
              colorImageUrl &&
              laydownImageUrl &&
              isValid)
          ) && (
            <Stack
              alignItems="center"
              spacing={2}
              sx={{
                left: '50%',
                position: 'absolute',
                top: '50%',
                transform: 'translate(-50%, -50%)'
              }}>
              <Typography variant="body1" color="text.secondary">
                📷 No preview available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {!isValid
                  ? 'Check the form for validation errors'
                  : 'Complete the configuration to see preview'}
              </Typography>
            </Stack>
          )}
        </Box>

        <Box sx={{backgroundColor: 'grey.50', p: '1rem'}}>
          <Typography variant="body2" color="text.secondary">
            {(() => {
              const usingFirstColor = !formValues.colorId && colors?.[0];

              if (formValues.useLabImage && previewUrl) {
                const baseMessage =
                  'Live preview of the current lab image configuration';
                if (usingFirstColor) {
                  return `${baseMessage}. Using first available color "${colors[0].name}" (${colors[0].id}) for preview since no specific color was selected.`;
                }
                return baseMessage;
              }
              if (!formValues.useLabImage && colorImageUrl && laydownImageUrl) {
                const baseMessage =
                  'Live preview showing initial product image and color image side by side. Product color will be set by user selection (e.g color swatch selection)';
                if (usingFirstColor) {
                  return `${baseMessage}. Using first available color "${colors[0].name}" (${colors[0].id}) for preview since no specific color was selected.`;
                }
                return baseMessage;
              }
              return 'Preview will appear here when configuration is complete';
            })()}
          </Typography>
        </Box>
      </Box>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={2}
        sx={{justifyContent: 'flex-end', mt: 2}}>
        <Button
          type="button"
          variant="secondary"
          onClick={onBack}
          disabled={isLoadingOrSubmitting}>
          Back
        </Button>

        <Button
          type="button"
          variant="tertiary"
          onClick={onSubmit}
          disabled={isLoadingOrSubmitting || !isValid}
          sx={{minWidth: '140px'}}>
          {isLoadingOrSubmitting ? 'Updating...' : 'Review & Update'}
        </Button>
      </Stack>
    </Box>
  );
}
